"use client"

import type React from "react"
import { useState } from "react"
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom"
import { useAuth } from "../../services/AuthContext"
import DatabaseStats from "./DatabaseStats"
import Dashboard from "./Dashboard"
import Analytics from "./Analytics"
import OrganizationDetails from "./OrganizationDetails"
import {
  Building,
  LineChartIcon as ChartLine,
  LogOut,
  Menu,
  X,
  Database,
  Crown,
  ChevronRight,
} from "lucide-react"

const SuperAdminLayout: React.FC = () => {
  const { userData, logout } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  const handleLogout = () => {
    logout()
    navigate("/login")
  }

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + "/")
  }

  if (!userData || userData.role !== "superadmin") {
    return <Navigate to="/login" replace />
  }

  const menuItems = [
    {
      name: "Dashboard",
      icon: Building,
      path: "/superadmin",
      exactMatch: true,
    },
    {
      name: "Analíticas",
      icon: ChartLine,
      path: "/superadmin/analytics",
      exactMatch: false,
    },
    {
      name: "Almacenamiento",
      icon: Database,
      path: "/superadmin/db-stats",
      exactMatch: false,
    },
  ]

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <div
        className={`bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-gray-100 transition-all duration-300 flex flex-col h-full shadow-2xl relative ${
          sidebarOpen ? "w-72" : "w-20"
        }`}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-700/50">
          <div className="flex items-center justify-between">
            <div className={`flex items-center gap-3 ${sidebarOpen ? "" : "hidden"}`}>
              <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                <Crown className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">ColStock</h2>
                <p className="text-xs text-gray-400">Super Admin Panel</p>
              </div>
            </div>
            <button
              className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors duration-200"
              onClick={toggleSidebar}
            >
              {sidebarOpen ? (
                <X className="h-5 w-5 text-gray-400" />
              ) : (
                <Menu className="h-5 w-5 text-gray-400" />
              )}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon
            const active = item.exactMatch 
              ? location.pathname === item.path
              : isActive(item.path)
            
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group relative ${
                  active
                    ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/30"
                    : "hover:bg-gray-700/50 text-gray-300 hover:text-white"
                }`}
              >
                <Icon className={`h-5 w-5 flex-shrink-0 ${active ? "text-white" : "text-gray-400 group-hover:text-white"}`} />
                <span className={`font-medium truncate ${sidebarOpen ? "" : "hidden"}`}>
                  {item.name}
                </span>
                {active && sidebarOpen && (
                  <ChevronRight className="h-4 w-4 ml-auto text-white" />
                )}
                
                {/* Tooltip for collapsed sidebar */}
                {!sidebarOpen && (
                  <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap shadow-xl z-50">
                    {item.name}
                    <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                  </div>
                )}
              </button>
            )
          })}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-gray-700/50">
          {/* User Info */}
          <div className={`mb-3 p-3 rounded-xl bg-gray-700/30 ${sidebarOpen ? "" : "flex justify-center"}`}>
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg flex-shrink-0">
                  <span className="text-lg font-bold text-white">
                    {userData.nombre.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-gray-900 rounded-full"></div>
              </div>
              <div className={`min-w-0 flex-1 ${sidebarOpen ? "" : "hidden"}`}>
                <p className="text-sm font-semibold text-white truncate">{userData.nombre}</p>
                <p className="text-xs text-gray-400 flex items-center gap-1">
                  <Crown className="w-3 h-3" />
                  Super Admin
                </p>
              </div>
            </div>
          </div>

          {/* Logout Button */}
          <button
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-300 hover:bg-red-500/10 hover:text-red-400 transition-all duration-200 group ${
              sidebarOpen ? "" : "justify-center"
            }`}
            onClick={handleLogout}
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            <span className={`font-medium ${sidebarOpen ? "" : "hidden"}`}>
              Cerrar Sesión
            </span>
            
            {/* Tooltip for collapsed sidebar */}
            {!sidebarOpen && (
              <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap shadow-xl z-50">
                Cerrar Sesión
                <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
              </div>
            )}
          </button>
        </div>

        {/* Decorative Element */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/organizations" element={<Dashboard />} />
          <Route path="/organization/:id" element={<OrganizationDetails />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/db-stats" element={<DatabaseStats />} />
          <Route path="*" element={<Navigate to="/superadmin" replace />} />
        </Routes>
      </div>
    </div>
  )
}

export default SuperAdminLayout